import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

function FamilyJsonLd() {
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/portfolio/family#webpage`,
      url: `${SITE_URL}/portfolio/family`,
      name: 'Family Photography | KAZU.PHOTO – 石川県金沢市 家族写真ギャラリー',
      description: '石川県金沢市のフォトグラファーKAZU.PHOTOによる家族写真ポートフォリオ。マタニティ・100日参り・七五三・入学など家族の大切な瞬間を記録。',
      inLanguage: 'ja',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Portfolio', item: `${SITE_URL}/portfolio/family` },
          { '@type': 'ListItem', position: 3, name: 'Family', item: `${SITE_URL}/portfolio/family` },
        ],
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'jsonld-family';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { script.parentNode?.removeChild(script); };
  }, []);
  return null;
}

export default function FamilyGallery() {
  const [scrolled, setScrolled] = useState(false);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 50); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const familyPhotos = [
    {
      id: 1,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/f86f039e8fbde1aa4851dee2f0182299.jpeg',
      title: '輝く未来',
    },
    {
      id: 2,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/5c761d0ba3ef4ab6c49b2bfe66b83b6e.jpeg',
      title: '新しい一日',
    },
    {
      id: 3,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/3c1a0d0fed14b8c5d1d50e1186a79e3e.jpeg',
      title: '入学祝い',
    },
    {
      id: 4,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/a6250f95e44ba972602cbc59cf9cee5a.jpeg',
      title: 'だんご',
    },
    {
      id: 5,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/544d9e52f04d1645870acebe32056f9c.jpeg',
      title: 'マタニティフォト',
    },
    {
      id: 6,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/e9d539c7ea202312dd9c3e4a646136ff.jpeg',
      title: '100日参り',
    },
    {
      id: 7,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/804e89b20e1647353eebe64015fc07dc.jpeg',
      title: '家族の笑顔',
    },
    {
      id: 8,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/d9128686a2c0d10e84582b48ee40e575.jpeg',
      title: '翔',
    },
    {
      id: 9,
      url: 'https://static.readdy.ai/image/bbbff43ff07d452d166ab0b0917c289c/480261e2932c20f7183e0ac8530d410f.jpeg',
      title: '愛おしい',
    },
  ];

  const openLightbox = (index: number) => {
    setSelectedImage(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (selectedImage === null) return;
    if (direction === 'prev') {
      setSelectedImage(selectedImage === 0 ? familyPhotos.length - 1 : selectedImage - 1);
    } else {
      setSelectedImage(selectedImage === familyPhotos.length - 1 ? 0 : selectedImage + 1);
    }
  };

  return (
    <div className="bg-white min-h-screen">
      <FamilyJsonLd />
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
          scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'
        }`}
      >
        <div className="flex items-center justify-between px-8 lg:px-16 py-6">
          <Link
            to="/"
            className="text-xs tracking-[0.2em] font-light text-stone-900 hover:text-stone-600 transition-colors"
          >
            KAZU.PHOTO
          </Link>
          <Link
            to="/"
            className="flex items-center gap-2 text-xs tracking-[0.2em] font-light text-stone-600 hover:text-stone-900 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-20 px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <span className="text-[10px] tracking-[0.3em] text-stone-400 uppercase mb-4 block">
            Portfolio
          </span>
          <h1 className="text-5xl md:text-6xl font-extralight tracking-wide text-stone-900 mb-4">
            Family
          </h1>
          <p className="text-sm text-stone-500 font-light tracking-wide">
            家族の絆と日常の輝き
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="pb-32 px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {familyPhotos.map((photo, index) => (
              <div
                key={photo.id}
                onClick={() => openLightbox(index)}
                className="group cursor-pointer relative overflow-hidden rounded-2xl bg-stone-100"
              >
                <div className="w-full flex items-center justify-center min-h-[300px] overflow-hidden">
                  <img
                    src={photo.url}
                    alt={photo.title}
                    className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <i className="ri-search-line text-white text-3xl" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white hover:text-stone-300 transition-colors z-10"
          >
            <i className="ri-close-line text-3xl" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('prev');
            }}
            className="absolute left-6 w-12 h-12 flex items-center justify-center text-white hover:text-stone-300 transition-colors z-10"
          >
            <i className="ri-arrow-left-line text-3xl" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              navigateImage('next');
            }}
            className="absolute right-6 w-12 h-12 flex items-center justify-center text-white hover:text-stone-300 transition-colors z-10"
          >
            <i className="ri-arrow-right-line text-3xl" />
          </button>

          <div
            className="max-w-6xl max-h-[90vh] px-16"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={familyPhotos[selectedImage].url}
              alt={familyPhotos[selectedImage].title}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <p className="text-center text-white text-sm mt-4 tracking-wide">
              {familyPhotos[selectedImage].title}
            </p>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-xs tracking-wider">
            {selectedImage + 1} / {familyPhotos.length}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-16 px-8 lg:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl font-extralight tracking-wide mb-2">KAZU.PHOTO</h3>
              <p className="text-xs text-white/60 tracking-wider">
                Photographer / Videografer
              </p>
            </div>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
          </div>
          <div className="pt-8 mt-8 border-t border-white/10 text-center">
            <p className="text-xs text-white/40">© 2025 KAZU.PHOTO</p>
          </div>
        </div>
      </footer>
    </div>
  );
}